// The city view's camera on the page (city zoom, Oct 9 2026): one CSS transform on the `world` layer (the scene painting
// and the buildings), driven by pinch, drag (with momentum), double-tap, the mouse wheel / trackpad
// and the keyboard. Pure maths in src/game/cameraMath.ts; this file is the input and the frame loop. Everything that has
// to follow the camera in screen space (plates, callouts, the construction pop-up) listens with `onFrame`.
import {
  OVERVIEW_REL, MAP_PULL_LOG, aboutPoint, clamp, clampCam, fitS, glideStep, keyZoom, lerpCam, mapPull, maxS, minS, overview, parseSavedView, relOf, releaseVelocity,
  rubber, rubberScale, saveOf, stepZoom, wheelLog, wheelZoom, whole, bounds, type Cam, type SavedView, type View,
} from "@/game/cameraMath";

export type FrameListener = (cam: Cam, view: View) => void;

const SAVE_KEY = "valor-web:city-view";

/** The view saved on this device (shared by every city). A view saved for the old city painting (unversioned, its centre
 *  in another frame) or a malformed one is dropped once, so the city opens at the whole city instead of somewhere odd. */
export function loadSavedView(): SavedView | null {
  try {
    const { view, stale } = parseSavedView(localStorage.getItem(SAVE_KEY));
    if (stale) localStorage.removeItem(SAVE_KEY);
    return view;
  } catch {
    return null;
  }
}
function storeView(v: SavedView) {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(v));
  } catch {
    /* private mode: kept for this visit */
  }
}

interface Anim {
  step(now: number): void;
}

/** Pointer travel (px) that turns a tap into a drag. */
export const DRAG_SLOP = 8;

export class CityCamera {
  cam: Cam = { s: 1, x: 0, y: 0 };
  view: View;
  private dirty = true;
  private anim: Anim | null = null;
  private raf = 0;
  private listeners = new Set<FrameListener>();
  private ptrs = new Map<number, { x: number; y: number }>();
  private g: null | {
    type: "pan" | "pinch";
    sx: number; sy: number; x0: number; y0: number; moved: boolean;
    hist: { t: number; x: number; y: number }[];
    d0?: number; s0?: number; wx?: number; wy?: number; mid?: { x: number; y: number };
  } = null;
  private suppress = false;
  private lastTap: { t: number; x: number; y: number } | null = null;
  private wheelT = 0;
  private ro: ResizeObserver | null = null;
  private readonly reduced: boolean;
  private off: (() => void)[] = [];
  /** set by the page: whether the camera may move (false while a sheet is open over it, for instance) */
  enabled = true;
  /** Zooming out past the overview pulls towards the world map (Ben, Oct 9): `onPull` hears how far (0..1, and whether a
   *  wheel did it — a wheel goes on its own at 1, fingers go when they let go), `onMap` is called to go there. */
  onPull: ((t: number, by: "touch" | "wheel" | "key") => void) | null = null;
  onMap: (() => void) | null = null;
  private pull = 0;
  private wheelPull = 0;
  private wheelPullT = 0;
  private setPull(t: number, by: "touch" | "wheel" | "key") {
    if (Math.abs(t - this.pull) < 1e-3 && !(t === 0 && this.pull !== 0)) return;
    this.pull = t;
    this.onPull?.(t, by);
  }
  private toMap() {
    this.pull = 0;
    this.wheelPull = 0;
    // reopen at the overview ("where I left it"), not at the rubber band's squeeze
    storeView(saveOf(this.view, overview(this.view)));
    this.onMap?.();
  }

  constructor(private vp: HTMLElement, private world: HTMLElement, W: number, H: number, X0: number, X1: number, minRel: number, maxRel: number) {
    const r = vp.getBoundingClientRect();
    this.view = { vw: Math.max(1, r.width || 390), vh: Math.max(1, r.height || 600), W, H, X0, X1, minRel, maxRel };
    this.reduced = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.bind();
    this.raf = requestAnimationFrame(this.frame);
  }

  // ------------------------------------------------------------------ public

  onFrame(fn: FrameListener): () => void {
    this.listeners.add(fn);
    this.dirty = true;
    return () => this.listeners.delete(fn);
  }

  /** Something on the page changed (new plates, new data): redraw the screen-space layer on the next frame. */
  refresh() {
    this.dirty = true;
  }

  set(c: Cam) {
    this.cam = { s: c.s, x: c.x, y: c.y };
    this.dirty = true;
  }

  get rel() {
    return relOf(this.view, this.cam);
  }

  animateTo(target: Cam, ms: number, done?: () => void) {
    if (this.reduced || !ms) {
      this.anim = null;
      this.set(target);
      this.settle();
      done?.();
      return;
    }
    const from = { ...this.cam };
    const t0 = performance.now();
    this.anim = {
      step: (now) => {
        const k = Math.min(1, (now - t0) / ms);
        this.set(lerpCam(from, target, k));
        if (k >= 1) {
          this.anim = null;
          this.settle();
          done?.();
        }
      },
    };
  }

  stop() {
    this.anim = null;
  }

  toWhole(ms = 320) {
    this.animateTo(whole(this.view), ms);
  }
  toOverview(ms = 320) {
    this.animateTo(overview(this.view), ms);
  }

  /** The world point under a screen point (px inside the viewport). */
  toWorld(px: number, py: number) {
    return { x: (px - this.cam.x) / this.cam.s, y: (py - this.cam.y) / this.cam.s };
  }

  /** The viewport changed size (rotation, a rail appearing): keep the same world point in the middle at the same zoom. */
  measure() {
    const r = this.vp.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    const old = this.view;
    if (Math.abs(old.vw - r.width) < 0.5 && Math.abs(old.vh - r.height) < 0.5) return;
    const rel = relOf(old, this.cam);
    const m = { x: (old.vw / 2 - this.cam.x) / this.cam.s, y: (old.vh / 2 - this.cam.y) / this.cam.s };
    this.view = { ...old, vw: r.width, vh: r.height };
    const s = fitS(this.view) * rel;
    this.set(clampCam(this.view, { s, x: this.view.vw / 2 - m.x * s, y: this.view.vh / 2 - m.y * s }));
  }

  setRange(minRel: number, maxRel: number) {
    this.view = { ...this.view, minRel, maxRel };
    this.set(clampCam(this.view, this.cam));
  }

  /** Save where the camera is (the city view reopens there when "City view opens at" is "where I left it"). */
  settle() {
    storeView(saveOf(this.view, this.cam));
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.wheelPullT);
    clearTimeout(this.wheelT);
    this.off.forEach((f) => f());
    this.ro?.disconnect();
    this.listeners.clear();
  }

  // ------------------------------------------------------------------ frame loop

  private moving = false;
  private lastMove = 0;
  private frame = (now: number) => {
    if (this.anim) this.anim.step(now);
    if (this.dirty) {
      this.dirty = false;
      const tf = `translate3d(${this.cam.x}px,${this.cam.y}px,0) scale(${this.cam.s})`;
      if (this.world.style.transform !== tf) {
        this.world.style.transform = tf;
        // a layer the browser can move cheaply while the camera moves; dropped once it rests so the city is redrawn
        // sharp at the new zoom (a will-change layer otherwise keeps the resolution it was first drawn at)
        if (!this.moving) {
          this.moving = true;
          this.world.classList.add("moving");
        }
        this.lastMove = now;
      }
      for (const fn of this.listeners) fn(this.cam, this.view);
    } else if (this.moving && now - this.lastMove > 180 && !this.g) {
      this.moving = false;
      this.world.classList.remove("moving");
    }
    this.raf = requestAnimationFrame(this.frame);
  };

  private glide(vx: number, vy: number) {
    if (this.reduced) {
      this.animateTo(clampCam(this.view, this.cam), 0);
      return;
    }
    let last = performance.now();
    let vel = { vx, vy };
    this.anim = {
      step: (now) => {
        const dt = Math.min(40, now - last);
        last = now;
        const st = glideStep(this.view, this.cam, vel, dt);
        vel = { vx: st.vx, vy: st.vy };
        this.set(st.cam);
        if (st.done) {
          this.anim = null;
          this.animateTo(clampCam(this.view, this.cam), 260);
        }
      },
    };
  }

  // ------------------------------------------------------------------ input

  private local(e: { clientX: number; clientY: number }) {
    const r = this.vp.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private startPan(pt: { x: number; y: number }, moved = false) {
    this.g = { type: "pan", sx: pt.x, sy: pt.y, x0: this.cam.x, y0: this.cam.y, moved, hist: [{ t: performance.now(), x: pt.x, y: pt.y }] };
  }

  private startPinch() {
    const [a, b] = [...this.ptrs.values()];
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    this.g = {
      type: "pinch", sx: mid.x, sy: mid.y, x0: this.cam.x, y0: this.cam.y, moved: true, hist: [],
      d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: this.cam.s, wx: (mid.x - this.cam.x) / this.cam.s, wy: (mid.y - this.cam.y) / this.cam.s, mid,
    };
    this.vp.classList.add("dragging");
  }

  private bind() {
    const vp = this.vp;
    const on = <K extends keyof HTMLElementEventMap>(el: HTMLElement | Window, type: K | string, fn: (e: never) => void, opts?: AddEventListenerOptions) => {
      el.addEventListener(type, fn as EventListener, opts);
      this.off.push(() => el.removeEventListener(type, fn as EventListener, opts));
    };

    on(vp, "pointerdown", (e: PointerEvent) => {
      this.suppress = false;
      if (!this.enabled) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      this.stop();
      const pt = this.local(e);
      this.ptrs.set(e.pointerId, pt);
      if (this.ptrs.size === 1) this.startPan(pt);
      else if (this.ptrs.size === 2) this.startPinch();
    });

    on(window, "pointermove", (e: PointerEvent) => {
      if (!this.ptrs.has(e.pointerId) || !this.g) return;
      const pt = this.local(e);
      this.ptrs.set(e.pointerId, pt);
      const g = this.g;
      if (g.type === "pan") {
        const dx = pt.x - g.sx;
        const dy = pt.y - g.sy;
        if (!g.moved && Math.hypot(dx, dy) > DRAG_SLOP) {
          g.moved = true;
          vp.classList.add("dragging");
        }
        if (!g.moved) return;
        const b = bounds(this.view, this.cam.s);
        this.set({ s: this.cam.s, x: rubber(g.x0 + dx, b.x0, b.x1), y: rubber(g.y0 + dy, b.y0, b.y1) });
        g.hist.push({ t: performance.now(), x: pt.x, y: pt.y });
        if (g.hist.length > 8) g.hist.shift();
      } else if (this.ptrs.size >= 2) {
        const [a, b2] = [...this.ptrs.values()];
        const mid = { x: (a.x + b2.x) / 2, y: (a.y + b2.y) / 2 };
        g.mid = mid;
        const raw = (g.s0! * Math.hypot(a.x - b2.x, a.y - b2.y)) / g.d0!;
        const s = rubberScale(raw, minS(this.view), maxS(this.view));
        this.setPull(mapPull(raw / fitS(this.view), this.view.minRel), "touch");
        const bb = bounds(this.view, s);
        this.set({ s, x: rubber(mid.x - g.wx! * s, bb.x0, bb.x1), y: rubber(mid.y - g.wy! * s, bb.y0, bb.y1) });
      }
    });

    const end = (e: PointerEvent) => {
      if (!this.ptrs.has(e.pointerId)) return;
      const pt = this.local(e);
      this.ptrs.delete(e.pointerId);
      const g = this.g;
      if (!g) return;
      if (g.type === "pinch") {
        if (this.pull >= 1 && e.type !== "pointercancel") {
          // a full pull towards the map: the first finger off goes (fingers seldom lift together)
          this.ptrs.clear();
          this.suppress = true;
          this.g = null;
          vp.classList.remove("dragging");
          this.toMap();
          return;
        }
        if (this.ptrs.size === 1) {
          // one finger lifted: carry on as a drag with the other (a pull towards the map is let go)
          this.setPull(0, "touch");
          this.startPan([...this.ptrs.values()][0], true);
          return;
        }
        this.setPull(0, "touch");
        const s = clamp(this.cam.s, minS(this.view), maxS(this.view));
        const m = g.mid ?? { x: this.view.vw / 2, y: this.view.vh / 2 };
        this.animateTo(clampCam(this.view, aboutPoint(this.cam, s, m.x, m.y)), 240);
        this.suppress = true;
        this.g = null;
        vp.classList.remove("dragging");
        return;
      }
      if (this.ptrs.size > 0) return;
      vp.classList.remove("dragging");
      if (g.moved) {
        this.suppress = true;
        const v = releaseVelocity(g.hist, performance.now(), pt.x, pt.y);
        if (Math.hypot(v.vx, v.vy) > 0.15 && e.type !== "pointercancel") this.glide(v.vx, v.vy);
        else this.animateTo(clampCam(this.view, this.cam), 240);
      } else if (e.pointerType !== "mouse" && !(e.target as Element | null)?.closest?.("button")) {
        // double-tap on open ground (buildings still open on a single tap)
        const t = performance.now();
        if (this.lastTap && t - this.lastTap.t < 320 && Math.hypot(pt.x - this.lastTap.x, pt.y - this.lastTap.y) < 30) {
          this.animateTo(stepZoom(this.view, this.cam, pt.x, pt.y), 300);
          this.lastTap = null;
        } else this.lastTap = { t, x: pt.x, y: pt.y };
      }
      this.g = null;
    };
    on(window, "pointerup", (e: PointerEvent) => {
      end(e);
      if (this.suppress) setTimeout(() => (this.suppress = false), 80);
    });
    on(window, "pointercancel", end);

    // a drag or a pinch is not a tap on whatever it started on
    on(vp, "click", (e: MouseEvent) => {
      if (this.suppress) {
        e.stopPropagation();
        e.preventDefault();
        this.suppress = false;
      }
    }, { capture: true });

    on(vp, "dblclick", (e: MouseEvent) => {
      if (!this.enabled || (e.target as Element).closest("button")) return;
      const pt = this.local(e);
      this.animateTo(stepZoom(this.view, this.cam, pt.x, pt.y), 300);
    });

    on(vp, "wheel", (e: WheelEvent) => {
      if (!this.enabled) return;
      e.preventDefault();
      this.stop();
      const pt = this.local(e);
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      // at the overview, more wheel-out adds up towards the world map; any zoom in lets it go
      const atMin = this.cam.s <= minS(this.view) * 1.002;
      if (dy > 0 && atMin && this.view.minRel <= OVERVIEW_REL + 1e-9) {
        this.wheelPull += wheelLog(dy, e.ctrlKey);
        const t = clamp(this.wheelPull / MAP_PULL_LOG, 0, 1);
        clearTimeout(this.wheelPullT);
        if (t >= 1) {
          this.setPull(1, "wheel");
          this.toMap();
          return;
        }
        this.setPull(t, "wheel");
        this.wheelPullT = window.setTimeout(() => {
          this.wheelPull = 0;
          this.setPull(0, "wheel");
        }, 450);
      } else if (this.wheelPull) {
        this.wheelPull = 0;
        clearTimeout(this.wheelPullT);
        this.setPull(0, "wheel");
      }
      this.set(wheelZoom(this.view, this.cam, dy, e.ctrlKey, pt.x, pt.y));
      clearTimeout(this.wheelT);
      this.wheelT = window.setTimeout(() => this.settle(), 200);
    }, { passive: false });

    // Safari's own pinch-to-zoom of the page
    for (const n of ["gesturestart", "gesturechange", "gestureend"]) on(vp, n, (e: Event) => e.preventDefault());

    // nothing inside the viewport may scroll it (scrollIntoView on a building would shift the world off the camera)
    on(vp, "scroll", () => {
      if (vp.scrollTop || vp.scrollLeft) {
        vp.scrollTop = 0;
        vp.scrollLeft = 0;
      }
    });

    on(vp, "keydown", (e: KeyboardEvent) => {
      if (!this.enabled || e.altKey || e.metaKey || e.ctrlKey) return;
      if ((e.target as Element) !== vp && (e.target as Element).closest("input,textarea,select")) return;
      const v = this.view;
      let t: Cam | null = null;
      if (e.key === "+" || e.key === "=") t = keyZoom(v, this.cam, 1);
      else if (e.key === "-" || e.key === "_") {
        // − at the overview: on out to the world map
        if (this.cam.s <= minS(v) * 1.002 && v.minRel <= OVERVIEW_REL + 1e-9) {
          e.preventDefault();
          this.setPull(1, "key");
          this.toMap();
          return;
        }
        t = keyZoom(v, this.cam, -1);
      }
      else if (e.key === "0") t = whole(v);
      else if (e.key === "9") t = overview(v);
      else if (e.key.startsWith("Arrow")) {
        const d = 70;
        t = clampCam(v, {
          s: this.cam.s,
          x: this.cam.x + (e.key === "ArrowLeft" ? d : e.key === "ArrowRight" ? -d : 0),
          y: this.cam.y + (e.key === "ArrowUp" ? d : e.key === "ArrowDown" ? -d : 0),
        });
      }
      if (!t) return;
      e.preventDefault();
      this.animateTo(t, e.key.startsWith("Arrow") ? 160 : 240);
    });

    if (typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(vp);
    }
  }

}
